import React, { useEffect } from 'react';
import { animateScroll as scroll } from 'react-scroll/modules';

import { IconTypes } from '@eika/icon';
import { LinkCard } from '@eika/link-panel';

import { StringKeyValue } from '../../../actions/types';
import { CalculatorReducer } from '../../../reducers/calculator';
import { CompletionReducer } from '../../../reducers/completion';
import ApplicationStatus from '../../../util/mappings/applicationStatus';
import LoanProduct from '../../../util/mappings/loanProduct';
import { TrackingStep, trackEvent } from '../../../util/piwik-utils';

interface Props {
    completion: CompletionReducer;
    calculator: CalculatorReducer;
    isEika: boolean;
    text: StringKeyValue;
    insuranceClick: () => void;
}

const LoanCompletionReceipt = ({ completion, text, calculator, isEika, insuranceClick }: Props): JSX.Element => {
    useEffect(() => {
        trackEvent(TrackingStep.CompletionReceipt);
        scroll.scrollToTop();
    }, []);

    const { applicationId, applicationStatus } = completion.ongoing;
    const {
        completionReceiptTitle,
        completionReceiptCaseNo,
        completionReceiptCompleteP1,
        completionReceiptCompleteP2,
        completionReceiptIncompleteP1,
        completionReceiptIncompleteP2,
    } = text;
    const isCar = calculator.loanInfo.loanProduct === LoanProduct.car;
    const carInsuranceLink = isEika ? '/forsikre/bil-og-andre-kjoretoy' : '/forsikre/bilforsikring';
    const insuranceBlockedHosts = ['eikadigitalbank.no', 'penni.no'];
    const currentHostname = window.location.hostname.toLowerCase();
    const hideInsuranceCard = insuranceBlockedHosts.some(
        (host) => currentHostname === host || currentHostname.endsWith(`.${host}`),
    );
    const showInsuranceCard = applicationStatus === ApplicationStatus.documentsComplete && isCar && !hideInsuranceCard;

    useEffect(() => {
        if (showInsuranceCard) {
            trackEvent(TrackingStep.CompletionReceiptInsuranceCard);
        }
    }, [showInsuranceCard]);

    const handleInsuranceClick = () => {
        trackEvent(TrackingStep.CompletionReceiptInsuranceCardClick);
        insuranceClick();
    };

    return (
        <div className="receipt">
            <h1>{completionReceiptTitle}</h1>

            {completionReceiptCaseNo && applicationId && (
                <p>
                    <strong>{completionReceiptCaseNo}</strong> {applicationId}
                </p>
            )}

            {applicationStatus === ApplicationStatus.documentsComplete ? (
                <>
                    {completionReceiptCompleteP1 && <p>{completionReceiptCompleteP1}</p>}
                    {completionReceiptCompleteP2 && <p>{completionReceiptCompleteP2}</p>}
                    {showInsuranceCard && (
                        <LinkCard
                            url={carInsuranceLink}
                            title={text.receiptApprovedInfoBoxTitle}
                            textOne={text.receiptApprovedInfoBoxLinkText}
                            icon={IconTypes.KJORETOY_36}
                            onClick={handleInsuranceClick}
                        />
                    )}
                </>
            ) : (
                <>
                    {completionReceiptIncompleteP1 && <p>{completionReceiptIncompleteP1}</p>}
                    {completionReceiptIncompleteP2 && <p>{completionReceiptIncompleteP2}</p>}
                </>
            )}
        </div>
    );
};

export default LoanCompletionReceipt;
